import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BreadCrumb } from '../../components/BreadCrumb';
import { Button } from '../../components/Button';
import { AutoCompleteSingleSelect } from '../../components/DropDown/AutoCompleteSingleSelect';
import { AutoCompleteMultiSelect } from '../../components/DropDown/AutoCompleteMultiSelect';
import { FileUploader } from '../../components/FileUploader';
import { Label } from '../../components/Label';
import { TextField } from '../../components/TextField';
import { addBrokerageOffice } from '../../redux/actions/admin';
import { getRoleBasedUsersApi } from '../../redux/apis/admin';
import {
  BREAD_CRUMB_LABELS,
  DEBOUNCE_TIMEOUT,
  IMAGE_FILE_TYPE,
  LABELS,
  REALTOR_QUERY_DATA,
  RESPONSE_STATUS_CODES,
  SALES_MANAGER_QUERY_DATA
} from '../../constants/common';
import { ROUTE_LIST } from '../../constants/routes';
import { addBrokerageOfficeSchema } from '../../schemas/adminSchema';
import { debounce } from '../../utils/helper';
import { PageLoader } from '../../components/PageLoader';
import { generatePresignedUrlApi, uploadFileApi } from '../../redux/apis/commonApi';

export const CreateBrokerageOffice = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [realtorData, setRealtorData] = useState([]);
  const [salesManagerData, setsalesManagerData] = useState();
  const [isAdded, setIsAdded] = useState(false);
  const [realtorOptions, setRealtorOptions] = useState([]);
  const [salesManagerOptions, setSalesManagerOptions] = useState();
  const [realtorSearch, setRealtorSearch] = useState('');
  const [salesManagerSearch, setsalesManagerSearch] = useState('');
  const { isSucceed, isLoading, isFailed } = useSelector((state) => state.apiStatus);
  const { brokerageOffice } = useSelector((state) => state.admin);
  const [isTexasOffice, setIsTexasOffice] = useState(false);
  const [isMultiOrCommercialOffice, setIsMultiOrCommercialOffice] = useState(false);

  const breadcrumbsLinks = [
    {
      label: BREAD_CRUMB_LABELS.DASHBOARD,
      to: ROUTE_LIST.DASHBOARD
    },
    {
      label: BREAD_CRUMB_LABELS.BROKERAGE_OFFICE,
      to: ROUTE_LIST.BROKERAGE_OFFICE
    },
    {
      label: BREAD_CRUMB_LABELS.CREATE_BROKERAGE_OFFICE,
      last: true
    }
  ];

  useEffect(() => {
    // Get realtor and sales manager data for options
    getRoleBasedUsersApi(REALTOR_QUERY_DATA).then((res) => setRealtorData(res.data.data.userData));
    getRoleBasedUsersApi(SALES_MANAGER_QUERY_DATA).then((res) =>
      setsalesManagerData(res.data.data.userData)
    );
  }, []);

  // redirect to brokerage office list after adding brokerage office
  useEffect(() => {
    if (isSucceed && isAdded && brokerageOffice?.isBrokerageOfficeAdded)
      navigate(ROUTE_LIST.BROKERAGE_OFFICE);
  }, [isSucceed, isAdded, brokerageOffice]);

  useEffect(() => {
    // realtor options
    setRealtorOptions(
      realtorData?.map((realtor) => {
        return {
          value: realtor.id,
          label: realtor.firstName + ' ' + realtor.lastName
        };
      })
    );
    // sales manager options
    setSalesManagerOptions(
      salesManagerData?.map((manager) => {
        return {
          value: manager.id,
          label: manager.firstName + ' ' + manager.lastName
        };
      })
    );
  }, [realtorData, salesManagerData]);

  const initialValues = {
    name: '',
    address: '',
    zipCode: '',
    realtors: [],
    salesManager: '',
    file: '',
    keapTagId: '',
    isTexas: false,
    isMultiOrCommercial: false
  };
  const { values, errors, handleChange, handleBlur, handleSubmit, touched, setFieldValue } =
    useFormik({
      initialValues,
      validationSchema: addBrokerageOfficeSchema,
      onSubmit: async (values) => {
        const presingedUrlData = await generatePresignedUrlApi({
          file: values.file[0],
          imageType: IMAGE_FILE_TYPE.BROKERAGE_OFFICE_LOGO
        });
        if (presingedUrlData?.status === RESPONSE_STATUS_CODES.OK) {
          const presignedUrl = presingedUrlData?.data.data.split('?')[0];
          const uploadFile = await uploadFileApi({
            file: values.file[0],
            url: presingedUrlData.data.data
          });
          if (uploadFile?.status === RESPONSE_STATUS_CODES.OK) {
            const brokerageOfficeData = { ...values };
            delete brokerageOfficeData.file;
            dispatch(
              addBrokerageOffice({
                ...brokerageOfficeData,
                presignedUrl
              })
            );
          }
        }
        if (!isFailed) setIsAdded(true);
      }
    });

  // Added for file upload
  const fileChangeHandler = (e) => {
    setSelectedFile(true);
    const fileData = e.target.files[0];
    setFiles([fileData]);
  };
  useEffect(() => {
    setFieldValue('file', files);
  }, [files]);

  // Search realtors based on timeout seconds
  const handleRealtorSearch = debounce((value) => {
    setRealtorSearch(value);
  }, DEBOUNCE_TIMEOUT);
  const realtorSearchHandler = () => {
    getRoleBasedUsersApi({
      ...REALTOR_QUERY_DATA,
      search: realtorSearch
    }).then((res) => setRealtorData(res.data.data.userData));
  };
  useEffect(() => {
    if (realtorSearch !== '' && realtorSearch !== 0) {
      realtorSearchHandler();
    }
  }, [realtorSearch]);

  // Search sales manager based on timeout seconds
  const handleSalesManagerSearch = debounce((value) => {
    setsalesManagerSearch(value);
  }, DEBOUNCE_TIMEOUT);
  const salesManagerSearchHandler = () => {
    getRoleBasedUsersApi({
      ...SALES_MANAGER_QUERY_DATA,
      search: salesManagerSearch
    }).then((res) => setsalesManagerData(res.data.data.userData));
  };
  useEffect(() => {
    if (salesManagerSearch !== '' && salesManagerSearch !== 0) {
      salesManagerSearchHandler();
    }
  }, [salesManagerSearch]);

  const isTexasHandler = (e) => {
    handleChange(e);
    if (isMultiOrCommercialOffice) {
      setIsTexasOffice(false);
    }
    setIsTexasOffice(true);
  };
  const isMultiOrCommercialHandler = (e) => {
    handleChange(e);
    if (isTexasOffice) {
      setIsMultiOrCommercialOffice(false);
    }
    setIsMultiOrCommercialOffice(true);
  };

  useEffect(() => {
    setFieldValue('isTexas', Boolean(isTexasOffice));
    setFieldValue('isMultiOrCommercial', Boolean(isMultiOrCommercialOffice));
  }, [isTexasOffice, isMultiOrCommercialOffice]);

  return (
    <>
      <div className="page-inner">
        <div className="page-content-block">
          <div className="inner-page-title-wrapper">
            <div className="title-left-block">
              <BreadCrumb links={breadcrumbsLinks} />
              <Label variant="h1" label={LABELS.CREATE_BROKERAGE_OFFICE} />
            </div>
          </div>
          <div className="white-box shadow-box">
            {isLoading && isAdded && <PageLoader />}
            <form onSubmit={handleSubmit}>
              <div className="form-row-wrapper">
                <div className="form-col has-required">
                  <TextField
                    fullWidth
                    id="name"
                    label="Name"
                    placeholder="Name"
                    name="name"
                    className="form-group"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </div>
                <div className="form-col">
                  <TextField
                    fullWidth
                    id="address"
                    label="Address"
                    placeholder="Address"
                    name="address"
                    className="form-group"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.address}
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </div>
                <div className="form-col">
                  <TextField
                    fullWidth
                    id="zipCode"
                    label="Zip Code"
                    placeholder="Zip Code"
                    name="zipCode"
                    className="form-group"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.zipCode}
                    error={touched.zipCode && Boolean(errors.zipCode)}
                    helperText={touched.zipCode && errors.zipCode}
                  />
                </div>
                <div
                  className={
                    touched.realtors && Boolean(errors.realtors) ? 'form-col error' : 'form-col'
                  }>
                  <AutoCompleteMultiSelect
                    freeSolo // user input is not bound with provided option. Added this because of getting warning of unmatched options.
                    filterOptions={(options) => options}
                    name="realtors"
                    className="multi-select"
                    options={realtorOptions}
                    label="Realtors"
                    classes={{ paper: 'custom-select-dropdown multiselect-dropdown' }}
                    placeholder={values.realtors.length > 0 ? '' : 'Realtors'}
                    onChange={(event, value) => {
                      if (!value || value.length === 0) {
                        // Clear the options when a all selected options are cleared
                        getRoleBasedUsersApi(REALTOR_QUERY_DATA).then((res) =>
                          setRealtorData(res.data.data.userData)
                        );
                      }
                      setFieldValue('realtors', value);
                    }}
                    onBlur={handleBlur}
                    value={values.realtors}
                    error={touched.realtors && Boolean(errors.realtors)}
                    helperText={touched.realtors && errors.realtors}
                    onInputChange={(e, value, reason) => {
                      handleRealtorSearch(e.target.value);
                      if (reason === 'reset') {
                        getRoleBasedUsersApi(REALTOR_QUERY_DATA).then((res) => {
                          setRealtorData(res.data.data.userData);
                        });
                      }
                    }}
                  />
                </div>
                <div
                  className={
                    touched.salesManager && Boolean(errors.salesManager)
                      ? 'form-col has-required error'
                      : 'form-col has-required'
                  }>
                  <AutoCompleteSingleSelect
                    placeholder="Sales Manager"
                    filterOptions={(options) => options}
                    options={salesManagerOptions}
                    label="Sales Manager"
                    classes={{ paper: 'custom-select-dropdown multiselect-dropdown' }}
                    name="salesManager"
                    onChange={(event, value) => {
                      value
                        ? setFieldValue('salesManager', value?.value)
                        : setFieldValue('salesManager', '');
                    }}
                    onBlur={handleBlur}
                    value={values.salesManager}
                    error={touched.salesManager && Boolean(errors.salesManager)}
                    helperText={touched.salesManager && errors.salesManager}
                    onInputChange={(e, value) => {
                      if (value === '') {
                        getRoleBasedUsersApi(SALES_MANAGER_QUERY_DATA).then((res) =>
                          setsalesManagerData(res.data.data.userData)
                        );
                      }
                      handleSalesManagerSearch(value);
                    }}
                  />
                </div>
                <div className="form-col has-required">
                  <TextField
                    fullWidth
                    id="keapTagId"
                    label="Keap Brokerage Subscribe Tag Id"
                    placeholder="Tag Id"
                    name="keapTagId"
                    className="form-group"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.keapTagId}
                    error={touched.keapTagId && Boolean(errors.keapTagId)}
                    helperText={touched.keapTagId && errors.keapTagId}
                  />
                </div>
                <div className="disclaimer-checkbox form-col">
                  <input
                    id="isTexas"
                    name="isTexas"
                    type="checkbox"
                    onChange={isTexasHandler}
                    value={values.isTexas}
                    checked={values.isTexas}
                  />
                  <label htmlFor="isTexas">Is Brokerage Office in Texas?</label>
                </div>
                <div className="disclaimer-checkbox form-col">
                  <input
                    id="isMultiOrCommercial"
                    name="isMultiOrCommercial"
                    type="checkbox"
                    onChange={isMultiOrCommercialHandler}
                    value={values.isMultiOrCommercial}
                    checked={values.isMultiOrCommercial}
                  />
                  <label htmlFor="isMultiOrCommercial">
                    Is Brokerage Office Multifamily or Commercial?
                  </label>
                </div>
                <div className="form-col full-width has-required">
                  <FileUploader
                    handleChange={fileChangeHandler}
                    setImagePreviewOpen={setImagePreviewOpen}
                    imagePreviewOpen={imagePreviewOpen}
                    selectedFile={selectedFile}
                    files={files}
                    label="Logo Upload (Image dimensions: 225*75)"
                    handleDelete={() => {
                      setFiles([]);
                      setSelectedFile(false);
                    }}
                    name="file"
                    onBlur={handleBlur}
                    error={touched.file && Boolean(errors.file)}
                    helperText={touched.file && errors.file}
                  />
                </div>
              </div>
              <div className="form-btn-wrapper">
                <Button
                  className="primary-btn border-btn"
                  name="Cancel"
                  onClick={() => navigate(ROUTE_LIST.BROKERAGE_OFFICE)}
                />
                <Button className="primary-btn" type="submit" name="+ Create" />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
